import { useState, useMemo } from 'react'
import { Plus, Fuel, TrendingUp, AlertTriangle, DollarSign } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  FuelTransactionsTable,
  FuelTransactionDrawer,
  DeleteFuelTransactionDialog,
  FuelCsvImportDialog,
} from '@/components/fuel-transactions'
import { RecordStatusPill } from '@/components/data-table'
import {
  useFuelTransactions,
  useDeleteFuelTransaction,
  useRestoreFuelTransaction,
  useFuelCommitmentProgress,
} from '@/hooks/useFuelTransactions'
import {
  formatFuelQuantity,
  formatFuelTotal,
  getDaysRemainingInMonth,
} from '@/types/fuel-transactions'
import { useAppContext } from '@/hooks/routing/useAppContext'
import type { RecordStatusFilter } from '@/types/record-status'

/**
 * Fuel Tracking management page
 *
 * Features:
 * - Summary stats cards (total gallons, revenue, transactions)
 * - Commitment status overview
 * - Full transactions table with filters
 */
export function FuelTrackingPage() {
  // Location context
  const { locationId } = useAppContext()

  // Record status filter
  const [recordStatus, setRecordStatus] = useState<RecordStatusFilter>('active')

  // Drawer/Dialog state
  const [drawerState, setDrawerState] = useState<{
    open: boolean
    fuelTransactionId?: string
  }>({ open: false })
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string
    transactionNumber: string
  } | null>(null)
  const [importDialogOpen, setImportDialogOpen] = useState(false)

  // Fetch fuel transactions
  const { data: fuelTransactions, isLoading } = useFuelTransactions(undefined, recordStatus)
  const { data: commitmentProgress } = useFuelCommitmentProgress()

  // Mutations
  const deleteFuelTransaction = useDeleteFuelTransaction()
  const restoreFuelTransaction = useRestoreFuelTransaction()

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (!fuelTransactions || fuelTransactions.length === 0) {
      return {
        monthlyGallons: 0,
        monthlyRevenue: 0,
        transactionCount: 0,
        ytdGallons: 0,
        ytdRevenue: 0,
      }
    }

    const now = new Date()
    const currentMonth = now.getMonth()
    const currentYear = now.getFullYear()

    let monthlyGallons = 0
    let monthlyRevenue = 0
    let ytdGallons = 0
    let ytdRevenue = 0

    fuelTransactions.forEach((tx) => {
      if (tx.status !== 'voided') {
        const txDate = new Date(tx.fueled_at)
        const txMonth = txDate.getMonth()
        const txYear = txDate.getFullYear()

        // YTD
        if (txYear === currentYear) {
          ytdGallons += tx.quantity_gallons
          ytdRevenue += tx.total_amount
        }

        // Current month
        if (txMonth === currentMonth && txYear === currentYear) {
          monthlyGallons += tx.quantity_gallons
          monthlyRevenue += tx.total_amount
        }
      }
    })

    return {
      monthlyGallons,
      monthlyRevenue,
      transactionCount: fuelTransactions.length,
      ytdGallons,
      ytdRevenue,
    }
  }, [fuelTransactions])

  // Calculate commitment stats
  const commitmentStats = useMemo(() => {
    if (!commitmentProgress || commitmentProgress.length === 0) {
      return { total: 0, atRisk: 0, onTrack: 0 }
    }

    const atRisk = commitmentProgress.filter((c) => c.isAtRisk).length
    return {
      total: commitmentProgress.length,
      atRisk,
      onTrack: commitmentProgress.length - atRisk,
    }
  }, [commitmentProgress])

  const daysRemaining = getDaysRemainingInMonth()

  // Handlers
  const handleCreate = () => {
    setDrawerState({ open: true, fuelTransactionId: undefined })
  }

  const handleEdit = (id: string) => {
    setDrawerState({ open: true, fuelTransactionId: id })
  }

  const handleDelete = (id: string, transactionNumber: string) => {
    setDeleteTarget({ id, transactionNumber })
  }

  const handleDrawerOpenChange = (open: boolean) => {
    setDrawerState((prev) => ({
      open,
      fuelTransactionId: open ? prev.fuelTransactionId : undefined,
    }))
  }

  const handleCloseDeleteDialog = (open: boolean) => {
    if (!open) {
      setDeleteTarget(null)
    }
  }

  const handleConfirmDelete = async () => {
    if (deleteTarget) {
      await deleteFuelTransaction.mutateAsync(deleteTarget.id)
      setDeleteTarget(null)
    }
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Fuel Tracking</h1>
          <p className="text-muted-foreground">
            Track fuel transactions and commitment progress
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RecordStatusPill value={recordStatus} onChange={setRecordStatus} />
          <Button onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add Fuel Transaction
          </Button>
        </div>
      </div>

      {/* Summary Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* This Month */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Month</CardTitle>
            <Fuel className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatFuelQuantity(summaryStats.monthlyGallons)}
            </div>
            <p className="text-xs text-muted-foreground">
              {formatFuelTotal(summaryStats.monthlyRevenue)} revenue
            </p>
          </CardContent>
        </Card>

        {/* YTD */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Year to Date</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatFuelQuantity(summaryStats.ytdGallons)}
            </div>
            <p className="text-xs text-muted-foreground">
              {formatFuelTotal(summaryStats.ytdRevenue)} revenue
            </p>
          </CardContent>
        </Card>

        {/* Commitments */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Commitments</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {commitmentStats.onTrack}/{commitmentStats.total}
            </div>
            <p className="text-xs text-muted-foreground">
              {commitmentStats.total > 0
                ? `${Math.round((commitmentStats.onTrack / commitmentStats.total) * 100)}% on track`
                : 'No active commitments'}
            </p>
          </CardContent>
        </Card>

        {/* At Risk */}
        <Card className={commitmentStats.atRisk > 0 ? 'border-amber-200 dark:border-amber-900' : ''}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">At Risk</CardTitle>
            <AlertTriangle
              className={`h-4 w-4 ${commitmentStats.atRisk > 0 ? 'text-amber-500' : 'text-muted-foreground'}`}
            />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${commitmentStats.atRisk > 0 ? 'text-amber-600' : ''}`}>
              {commitmentStats.atRisk}
            </div>
            <p className="text-xs text-muted-foreground">
              {daysRemaining} days remaining in month
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Fuel Transactions Table */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">All Transactions</h2>
        <FuelTransactionsTable
          data={fuelTransactions || []}
          isLoading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onRestore={(id) => restoreFuelTransaction.mutate(id)}
          onImportCsv={() => setImportDialogOpen(true)}
        />
      </div>

      {/* Create/Edit Fuel Transaction Drawer */}
      <FuelTransactionDrawer
        open={drawerState.open}
        onOpenChange={handleDrawerOpenChange}
        fuelTransactionId={drawerState.fuelTransactionId}
        locationId={locationId}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteFuelTransactionDialog
        open={!!deleteTarget}
        onOpenChange={handleCloseDeleteDialog}
        transactionNumber={deleteTarget?.transactionNumber ?? ''}
        onConfirm={handleConfirmDelete}
        isDeleting={deleteFuelTransaction.isPending}
      />

      {/* CSV Import Dialog */}
      <FuelCsvImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
      />
    </div>
  )
}
